import { cn } from "@/lib/utils";
import { forwardRef, useEffect, type HTMLAttributes } from "react";
import { useAtom } from "jotai";
import { isSidebarExpandedAtom } from "../store";

export const ChannelRootContainer = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    previewMode?: "desktop" | "mobile" | undefined;
    readOnly?: boolean;
  }
>(({ children, previewMode, className, readOnly = false, ...rest }, ref) => (
  <div
    className={cn(
      // Allow left/right element menus and popovers to escape horizontally
      // while keeping the main layout vertically clipped.
      "courier-flex courier-flex-1 courier-overflow-y-hidden courier-overflow-x-visible courier-root-container courier-relative",
      readOnly && "courier-editor-readonly",
      previewMode && "courier-editor-preview-mode",
      previewMode === "mobile" && "courier-editor-preview-mode-mobile",
      className
    )}
    {...rest}
    ref={ref}
  >
    {children}
  </div>
));

export const EditorSidebar = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    previewMode?: "desktop" | "mobile" | undefined;
    skipExpanded?: boolean;
    collapsed?: boolean;
    width?: string;
  }
>(
  (
    { children, className, previewMode, skipExpanded = false, collapsed = false, width, ...rest },
    ref
  ) => {
    const [isExpanded, setIsSidebarExpanded] = useAtom(isSidebarExpandedAtom);

    useEffect(() => {
      if (collapsed && isExpanded) {
        setIsSidebarExpanded(false);
      }
    }, [collapsed, isExpanded, setIsSidebarExpanded]);

    const isExpandedState = skipExpanded || collapsed ? false : isExpanded;
    const sidebarWidth = width || "courier-w-64";

    if (previewMode) {
      return null;
    }

    return (
      <>
        {/* Backdrop mask - only visible when expanded */}
        {isExpandedState && (
          <div
            className="courier-absolute courier-inset-0 courier-bg-black/50 courier-z-[40] courier-transition-opacity courier-duration-300 courier-cursor-pointer"
            onClick={() => setIsSidebarExpanded(false)}
            aria-hidden="true"
          />
        )}

        {/* Static sidebar container - maintains layout space */}
        <div
          className={cn(
            "courier-editor-sidebar-container courier-flex-shrink-0 courier-self-stretch",
            !isExpandedState && "courier-relative",
            sidebarWidth,
            className
          )}
        >
          {/* Actual sidebar - always absolute, slides and resizes */}
          <div
            className={cn(
              "courier-editor-sidebar courier-absolute courier-top-0 courier-bottom-0 courier-right-0 courier-p-4 courier-h-full courier-overflow-y-auto",
              "courier-transition-all courier-duration-300 courier-ease-in-out",
              isExpandedState
                ? "courier-w-[85%] courier-z-[50] courier-shadow-xl courier-bg-background courier-flex courier-flex-col"
                : sidebarWidth
            )}
            {...rest}
            ref={ref}
          >
            {children}
          </div>
        </div>
      </>
    );
  }
);
